import type { ReactElement } from 'react';

import { cx } from '../../lib/cx';
import styles from './icon.module.css';

/**
 * Набор иконок интерфейса.
 *
 * Имена — по ДЕЙСТВИЮ, а не по рисунку (`dispose`, а не `trash`): рисунок однажды
 * поменяется, а смысл кнопки нет, и переименовывать все места вызова из-за смены
 * начертания — работа на ровном месте.
 */
export type IconName =
  | 'void'
  | 'dispose'
  | 'undispose'
  | 'reprint'
  | 'edit'
  | 'block'
  | 'unblock'
  | 'delete'
  | 'restore'
  | 'key'
  | 'search'
  | 'history'
  | 'chevronDown'
  | 'chevronLeft'
  | 'chevronRight'
  | 'calendar'
  | 'print'
  | 'board'
  | 'check'
  | 'backspace'
  | 'dialpad'
  | 'settings'
  | 'refresh'
  | 'viewAs'
  | 'bell'
  | 'add'
  | 'close';

/**
 * Пути взяты из Tabler Icons (MIT), сетка 24×24, обводка — не заливка.
 *
 * Рисовались вручную и выглядели самодельно: рукописные пути почти никогда не попадают
 * в оптический баланс — штрихи разной длины, скругления вразнобой, оптический центр
 * плывёт от глифа к глифу. Владелец 2026-09-08: «иконки некрасивые».
 *
 * СКОПИРОВАНЫ, а не подключены пакетом. Причина та же, по которой набор был свой:
 * приложение работает офлайн и живёт в Capacitor-оболочке, где бандл едет по воздуху,
 * а нужно ровно семнадцать глифов. Пакет `@tabler/icons` ставится dev-зависимостью
 * только чтобы их извлечь (`scripts/build-icons.mjs`), в сборку не попадает.
 *
 * Обводка `currentColor` — иконка наследует цвет текста кнопки, и приглушённое
 * состояние получается само, из `color: var(--ds-muted)`. С заливкой пришлось бы
 * дублировать каждое состояние отдельным цветом.
 */
const PATHS: Record<IconName, ReactElement> = {
  // Аннулировать — перечёркнутый круг: действие отменяет саму ЗАПИСЬ, а не выбрасывает
  // продукт. Корзина здесь читалась бы как списание, а это разные операции.
  void: (
    <>
      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
      <path d="M5.7 5.7l12.6 12.6" />
    </>
  ),
  dispose: (
    <>
      <path d="M4 7l16 0" />
      <path d="M10 11l0 6" />
      <path d="M14 11l0 6" />
      <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
      <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
    </>
  ),
  // Отменить списание — стрелка назад, тот же жест «вернуть», что у undo.
  undispose: (
    <>
      <path d="M9 14l-4 -4l4 -4" />
      <path d="M5 10h11a4 4 0 1 1 0 8h-1" />
    </>
  ),
  // Удалить сотрудника в корзину и вернуть из неё (п.10). Рисунки те же, что
  // у списания, а имена другие: имена здесь по ДЕЙСТВИЮ, и `dispose` над строкой
  // человека читалось бы как «списать сотрудника».
  delete: (
    <>
      <path d="M4 7l16 0" />
      <path d="M10 11l0 6" />
      <path d="M14 11l0 6" />
      <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
      <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
    </>
  ),
  // Убрать позицию из списка — крестик, а не корзина: корзина на экране списания
  // читалась бы вторым «списать», тогда как смысл противоположный — НЕ списывать.
  close: (
    <>
      <path d="M18 6l-12 12" />
      <path d="M6 6l12 12" />
    </>
  ),
  restore: (
    <>
      <path d="M9 14l-4 -4l4 -4" />
      <path d="M5 10h11a4 4 0 1 1 0 8h-1" />
    </>
  ),
  reprint: (
    <>
      <path d="M19.933 13.041a8 8 0 1 1 -9.925 -8.788c3.899 -1 7.935 1.007 9.425 4.747" />
      <path d="M20 4v5h-5" />
    </>
  ),
  edit: (
    <>
      <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
      <path d="M13.5 6.5l4 4" />
    </>
  ),
  block: (
    <>
      <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-6" />
      <path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
      <path d="M8 11v-4a4 4 0 1 1 8 0v4" />
    </>
  ),
  // Разблокировать — та же скоба, но откинутая: состояние читается формой, а не цветом.
  unblock: (
    <>
      <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2l0 -6" />
      <path d="M11 16a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M8 11v-5a4 4 0 0 1 8 0" />
    </>
  ),
  key: (
    <>
      <path d="M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1 -4.069 0l-.301 -.301l-6.558 6.558a2 2 0 0 1 -1.239 .578l-.175 .008h-1.172a1 1 0 0 1 -.993 -.883l-.007 -.117v-1.172a2 2 0 0 1 .467 -1.284l.119 -.13l.414 -.414h2v-2h2v-2l2.144 -2.144l-.301 -.301a2.877 2.877 0 0 1 0 -4.069l2.643 -2.643a2.877 2.877 0 0 1 4.069 0" />
      <path d="M15 9h.01" />
    </>
  ),
  search: (
    <>
      <path d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
      <path d="M21 21l-6 -6" />
    </>
  ),
  // История — циферблат со стрелкой: «что происходило с записью во времени».
  history: (
    <>
      <path d="M12 8l0 4l2 2" />
      <path d="M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5" />
    </>
  ),
  // Стрелка выпадающего списка. Своя, а не системная: нативная рисуется поверх
  // нашей рамки средствами ОС, в своём размере и цвете, и три таких контрола подряд
  // читаются как неоформленная админка посреди оформленной страницы.
  chevronDown: <path d="M6 9l6 6l6 -6" />,
  chevronLeft: <path d="M15 6l-6 6l6 6" />,
  chevronRight: <path d="M9 6l6 6l-6 6" />,
  // Принтер. Прежде здесь стоял символ Unicode `⎙` (U+2399) — его нет в IBM Plex,
  // и браузер подставлял глиф из системного шрифта: на каждой платформе свой,
  // а где не нашёл — пустой квадрат.
  print: (
    <>
      <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
      <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
      <path d="M7 15a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2l0 -4" />
    </>
  ),
  // Табло: сетка плиток. Было `▦` (U+25A6) — та же беда, рисовалось рябым квадратом.
  board: (
    <>
      <path d="M4 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4" />
      <path d="M14 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4" />
      <path d="M4 15a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4" />
      <path d="M14 15a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4" />
    </>
  ),
  calendar: (
    <>
      <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12" />
      <path d="M16 3v4" />
      <path d="M8 3v4" />
      <path d="M4 11h16" />
      <path d="M11 15h1" />
      <path d="M12 15v3" />
    </>
  ),
  // Галочка: шаг онбординга, выполненный пункт, подтверждение — «сделано».
  // Отдельно от `undispose` (стрелка «вернуть»): та про откат действия, а не про
  // его завершённость.
  check: <path d="M5 12l5 5l10 -10" />,
  // Стереть последнюю цифру на клавиатуре PIN (T-040). Клавиша, а не «удалить»:
  // стирает один символ ввода, а не запись.
  backspace: (
    <>
      <path d="M20 6a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-11l-5 -5a1.5 1.5 0 0 1 0 -2l5 -5l11 0" />
      <path d="M12 10l4 4m0 -4l-4 4" />
    </>
  ),
  // PIN для входа на планшете точки (T-040): цифровая клавиатура — то, чем PIN вводят.
  // Отдельно от `key` (пароль): у сотрудника есть и то и другое, и кнопки рядом.
  // Настройка устройства (принтер на планшете). Шестерёнка, а не второй принтер:
  // рядом стоит «Печать» с принтером, и два одинаковых глифа в одной полосе читались
  // как один раздел (владелец, 2026-09-21).
  // «Войти как…» — саппорт-сессия платформы (T-039): смотреть глазами пользователя.
  viewAs: (
    <>
      <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
      <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
    </>
  ),
  settings: (
    <>
      <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065" />
      <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
    </>
  ),
  dialpad: (
    <>
      <path d="M4 3h2a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1" />
      <path d="M18 3h2a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1" />
      <path d="M11 3h2a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1" />
      <path d="M4 10h2a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1" />
      <path d="M18 10h2a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1" />
      <path d="M11 10h2a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1" />
      <path d="M11 17h2a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1" />
    </>
  ),
  // Обновить — две дуги по кругу со стрелками, а не одна: с одной стрелкой символ
  // читается как «вернуть» (тот же жест, что у `undispose`) и путается с ним.
  refresh: (
    <>
      <path d="M4 12a8 8 0 0 1 14.5-4.5" />
      <path d="M18.5 3v4.5H14" />
      <path d="M20 12a8 8 0 0 1-14.5 4.5" />
      <path d="M5.5 21v-4.5H10" />
    </>
  ),
  // Уведомления в Telegram на кухне (Tabler «bell»).
  bell: (
    <>
      <path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6" />
      <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
    </>
  ),
  // Добавить в меню позицию каталога сети (T-045, блок «Новое в каталоге сети»).
  // Плюс, а не галочка: действие ещё не сделано, галочка читалась бы как «уже в меню».
  add: (
    <>
      <path d="M12 5l0 14" />
      <path d="M5 12l14 0" />
    </>
  ),
};

export interface IconProps {
  name: IconName;
  /** Размер стороны в пикселях. По умолчанию 20 — рабочий размер в таблицах. */
  size?: number;
  className?: string;
}

/**
 * Иконка интерфейса.
 *
 * Пути в коде, а не библиотека в бандле: приложение работает офлайн и живёт
 * в Capacitor-оболочке, где каждый килобайт едет по воздуху. Семнадцать глифов кодом
 * весят ноль и не тянут зависимость ради семнадцати рисунков — рисунки при этом
 * не самодельные, а из Tabler (MIT).
 *
 * ВСЕГДА `aria-hidden`. Иконка не бывает единственным носителем смысла: подпись живёт
 * на кнопке, которая её содержит (`aria-label`), а продублированная здесь читалась бы
 * скринридером дважды. То же правило, что у статусов в `DESIGN.md`.
 */
export function Icon({ name, size = 20, className }: IconProps): ReactElement {
  return (
    <svg
      className={cx(styles.icon, className)}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
